import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, ArrowDownRight, CalendarClock } from "lucide-react";

const NAMES = { creator: "Creator", growth: "E-commerce Growth", agency: "Agency" };
const fmt = (s) => (s ? new Date(s).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" }) : "the end of this period");

// Upgrades apply now with a prorated charge; downgrades are scheduled for the end of the paid period.
export default function ChangePlanDialog({ lookupKey, onClose, onNoSubscription }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!lookupKey) return;
    setPreview(null);
    api.get(`/billing/preview-change?lookup_key=${lookupKey}`).then((r) => setPreview(r.data)).catch((e) => {
      if (e?.response?.status === 400) { onClose(); onNoSubscription(lookupKey); }
      else { toast.error(e?.response?.data?.detail || "Could not load plan change"); onClose(); }
    });
  }, [lookupKey]); // eslint-disable-line
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/change-plan", { lookup_key: lookupKey });
      toast.success(r.data.effective === "now" ? `Upgraded to ${NAMES[r.data.plan_tier] || r.data.plan_tier}` : `Downgrade scheduled for ${fmt(r.data.pending_plan_change?.effective_at)}`);
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change plan"); setBusy(false); }
  };
  return (
    <Dialog open={!!lookupKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card border-border" data-testid="change-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {preview?.upgrade === false ? <ArrowDownRight className="h-5 w-5 text-amber-300" /> : <ArrowUpRight className="h-5 w-5 text-emerald-400" />}
            {preview ? `${preview.upgrade ? "Upgrade" : "Downgrade"} to ${NAMES[preview.new_tier] || preview.new_tier}` : "Change plan"}
          </DialogTitle>
          <DialogDescription>Plan price: {preview ? `$${preview.new_amount}` : "…"}</DialogDescription>
        </DialogHeader>
        {!preview ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-4">
            {preview.upgrade ? (
              <p className="text-sm" data-testid="change-plan-proration">New features unlock right away. You're charged the prorated difference now
                {preview.preview_error ? "." : <>: <span className="font-semibold">${preview.amount_due_now.toFixed(2)}</span>.</>}</p>
            ) : (
              <p className="text-sm flex gap-2" data-testid="change-plan-period-end"><CalendarClock className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
                You keep your current plan until {fmt(preview.period_end)}. The lower price starts on your next bill. No partial refunds.</p>
            )}
            <Button onClick={confirm} disabled={busy} className="w-full bg-primary hover:bg-primary/90 text-white" data-testid="change-plan-confirm">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {preview.upgrade ? "Upgrade now" : "Schedule downgrade"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const PendingChangeBanner = () => {
  const { user } = useAuth();
  const p = user?.pending_plan_change;
  if (!p) return null;
  const undo = async () => {
    try { await api.post("/billing/cancel-downgrade"); toast.success("Downgrade cancelled"); setTimeout(() => window.location.reload(), 700); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="pending-downgrade-banner">
      <p className="text-sm">Switching to <span className="font-semibold">{NAMES[p.tier] || p.tier}</span> on {fmt(p.effective_at)}. You keep your current features until then.</p>
      <Button size="sm" variant="outline" className="border-amber-500/40" onClick={undo} data-testid="cancel-downgrade-btn">Keep current plan</Button>
    </div>
  );
};
